import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import buildVersion from "@/buildVersion.json";

const SUMMARY_ID = "version-debug-summary-json";

async function fetchServerVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

async function readSwState() {
  if (!("serviceWorker" in navigator)) {
    return { supported: false };
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { supported: true, registered: false };
    const pickWorkerState = (w) => (w ? { state: w.state, scriptURL: w.scriptURL } : null);
    return {
      supported: true,
      registered: true,
      scope: reg.scope,
      updateViaCache: reg.updateViaCache,
      active: pickWorkerState(reg.active),
      installing: pickWorkerState(reg.installing),
      waiting: pickWorkerState(reg.waiting),
      controller: navigator.serviceWorker.controller
        ? { state: navigator.serviceWorker.controller.state, scriptURL: navigator.serviceWorker.controller.scriptURL }
        : null,
    };
  } catch (e) {
    return { supported: true, error: String(e?.message || e) };
  }
}

async function clearAllCachesAndReload() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  try { localStorage.removeItem("sion_app_version"); } catch { /* ignore */ }
  const url = window.location.origin + "/?reload=" + Date.now();
  window.location.href = url;
}

export default function VersionDebug() {
  const navigate = useNavigate();
  const [server, setServer] = useState({ loading: true });
  const [sw, setSw] = useState({ loading: true });
  const [lastCheck, setLastCheck] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copyState, setCopyState] = useState("idle"); // idle | copied | failed

  const refresh = useCallback(async () => {
    setServer({ loading: true });
    setSw({ loading: true });
    const [s, w] = await Promise.all([fetchServerVersion(), readSwState()]);
    setServer(s);
    setSw(w);
    try {
      setLastCheck(window.__sion_last_update_check || null);
    } catch { setLastCheck(null); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const myVersion = buildVersion?.version || "(unknown)";
  const myBuildTime = buildVersion?.buildTime || "(unknown)";
  const serverVersion = server?.data?.version || (server.loading ? "(loading…)" : "(unavailable)");
  const serverBuildTime = server?.data?.buildTime || (server.loading ? "" : "");
  const isMismatch = server?.data?.version && server.data.version !== myVersion;

  const summary = {
    timestamp: new Date().toISOString(),
    bundle: { version: myVersion, buildTime: myBuildTime },
    server: server?.data ? { ...server.data } : { error: server?.error || "loading" },
    last_update_check: lastCheck,
    last_seen_server_version: (typeof window !== "undefined" ? window.__sion_last_seen_server_version : null) || null,
    mismatch: !!isMismatch,
    service_worker: sw,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    standalone_pwa: typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator?.standalone === true),
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    url: typeof window !== "undefined" ? window.location.href : "",
  };

  const handleCopySummary = async () => {
    const text = JSON.stringify(summary, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older mobile browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1800);
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 1800);
    }
  };

  const cardStyle = {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4 };
  const valueStyle = { fontSize: 14, color: "#111827", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all" };
  const rowStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 };

  return (
    <div data-testid="version-debug-page" style={{ minHeight: "100vh", background: "#F9FAFB", padding: "24px 16px 80px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Version Diagnostics</h1>
          <button
            data-testid="version-debug-back-btn"
            onClick={() => navigate(-1)}
            style={{ background: "transparent", border: "1px solid #D1D5DB", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "#374151", cursor: "pointer" }}
          >
            Back
          </button>
        </div>

        <div data-testid="version-debug-mismatch-banner" style={{
          ...cardStyle,
          background: isMismatch ? "#FEF3C7" : "#ECFDF5",
          borderColor: isMismatch ? "#F59E0B" : "#10B981",
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isMismatch ? "#92400E" : "#065F46" }}>
            {server.loading
              ? "Checking server…"
              : isMismatch
                ? "Update available — this device is on an older bundle."
                : server?.data
                  ? "Up to date. This device is running the latest deployed version."
                  : "Unable to reach the server. Network issue?"}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={rowStyle}>
            <div>
              <div style={labelStyle}>This device — bundle version</div>
              <div data-testid="version-debug-bundle-version" style={valueStyle}>{myVersion}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{myBuildTime}</div>
            </div>
            <div>
              <div style={labelStyle}>Server — latest version</div>
              <div data-testid="version-debug-server-version" style={{ ...valueStyle, color: isMismatch ? "#92400E" : "#111827" }}>{serverVersion}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{serverBuildTime}</div>
            </div>
          </div>

          <div style={{ borderTop: "1px dashed #E5E7EB", paddingTop: 16 }}>
            <div style={labelStyle}>Last UpdateGate check</div>
            <div data-testid="version-debug-last-check" style={valueStyle}>{lastCheck || "(never — this page was loaded without the check having run yet)"}</div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 12 }}>Service worker</div>
          {sw.loading ? (
            <div style={{ fontSize: 13, color: "#6B7280" }}>Loading…</div>
          ) : !sw.supported ? (
            <div style={{ fontSize: 13, color: "#DC2626" }}>This browser does not support service workers.</div>
          ) : !sw.registered ? (
            <div style={{ fontSize: 13, color: "#92400E" }}>No service worker is registered for this origin.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <div style={labelStyle}>Scope</div>
                <div data-testid="version-debug-sw-scope" style={valueStyle}>{sw.scope || "—"}</div>
              </div>
              <div>
                <div style={labelStyle}>updateViaCache</div>
                <div data-testid="version-debug-sw-uvc" style={valueStyle}>{sw.updateViaCache || "—"}</div>
              </div>
              <div>
                <div style={labelStyle}>Active worker</div>
                <div data-testid="version-debug-sw-active-state" style={valueStyle}>{sw.active?.state || "—"}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{sw.active?.scriptURL || ""}</div>
              </div>
              <div>
                <div style={labelStyle}>Installing / Waiting</div>
                <div style={valueStyle}>
                  installing: {sw.installing?.state || "—"} · waiting: {sw.waiting?.state || "—"}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Controlling this page</div>
                <div data-testid="version-debug-sw-controller" style={valueStyle}>{sw.controller ? `Yes (${sw.controller.state})` : "No"}</div>
              </div>
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Support summary (JSON)</div>
          <pre id={SUMMARY_ID} data-testid="version-debug-summary-pre" style={{ fontSize: 11, color: "#374151", background: "#F3F4F6", padding: 12, borderRadius: 8, maxHeight: 220, overflow: "auto", margin: 0 }}>
            {JSON.stringify(summary, null, 2)}
          </pre>
          <button
            data-testid="version-debug-copy-btn"
            onClick={handleCopySummary}
            style={{ marginTop: 12, background: "#111827", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%", maxWidth: 280 }}
          >
            {copyState === "copied" ? "Copied ✓" : copyState === "failed" ? "Copy failed — long-press the box above" : "Copy summary to clipboard"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
          <button
            data-testid="version-debug-refresh-btn"
            onClick={refresh}
            style={{ background: "transparent", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 16px", fontSize: 13, color: "#374151", cursor: "pointer" }}
          >
            Re-check now
          </button>
          <button
            data-testid="version-debug-force-update-btn"
            onClick={() => setConfirmOpen(true)}
            style={{ background: "#E05C2A", color: "#fff", border: "none", borderRadius: 8, padding: "12px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Force Update Now
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div
          data-testid="version-debug-confirm-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)", zIndex: 50000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 380, width: "100%", boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: "0 0 10px 0" }}>Force update this device?</h2>
            <p style={{ fontSize: 13, lineHeight: 1.55, color: "#6B7280", margin: "0 0 20px 0" }}>
              This will clear cached app data, unregister the service worker and reload the page. Any unsaved changes — including in-progress quotes, photos or job notes — will be lost. Continue?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                data-testid="version-debug-confirm-cancel"
                onClick={() => setConfirmOpen(false)}
                style={{ background: "transparent", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                data-testid="version-debug-confirm-go"
                onClick={clearAllCachesAndReload}
                style={{ background: "#E05C2A", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Yes, force update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
